import React, { useEffect, useRef } from 'react';

import { Box, useTheme } from '@mui/material';

const WAVEFORM_HISTORY_SIZE = 64;
const WAVEFORM_BAR_GAP = 2;
const WAVEFORM_SAMPLE_INTERVAL_MS = 40;

interface RecordingWaveformProps {
  stream: MediaStream;
}

export default function RecordingWaveform({ stream }: RecordingWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const barColor = theme.palette.primary.main;

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return undefined;
    }

    const AudioCtx =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) {
      return undefined;
    }

    let audioCtx: AudioContext;
    let source: MediaStreamAudioSourceNode;
    let analyser: AnalyserNode;
    try {
      audioCtx = new AudioCtx();
      source = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
    } catch {
      return undefined;
    }

    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    const history: Array<number> = new Array(WAVEFORM_HISTORY_SIZE).fill(0);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      audioCtx.close().catch(() => undefined);
      return undefined;
    }

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const cssWidth = container.clientWidth || 1;
      const cssHeight = container.clientHeight || 1;
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    let frameId = 0;
    let lastSampleAt = 0;
    const draw = (timestamp: number) => {
      if (timestamp - lastSampleAt >= WAVEFORM_SAMPLE_INTERVAL_MS) {
        analyser.getByteTimeDomainData(dataArray);

        let sumSquares = 0;
        for (let i = 0; i < bufferLength; i++) {
          const normalized = (dataArray[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }
        const rms = Math.sqrt(sumSquares / bufferLength);
        const amplitude = Math.min(rms * 5, 1);

        history.push(amplitude);
        if (history.length > WAVEFORM_HISTORY_SIZE) {
          history.shift();
        }
        lastSampleAt = timestamp;
      }

      const width = container.clientWidth;
      const height = container.clientHeight;
      ctx.clearRect(0, 0, width, height);

      const slot = width / WAVEFORM_HISTORY_SIZE;
      const barWidth = Math.max(slot - WAVEFORM_BAR_GAP, 1);
      const centerY = height / 2;

      ctx.fillStyle = barColor;
      for (let i = 0; i < history.length; i++) {
        const value = history[i];
        const barHeight = Math.max(value * (height - 4), 2);
        const x = i * slot;
        const y = centerY - barHeight / 2;
        const radius = barWidth / 2;
        ctx.beginPath();
        const ctxWithRoundRect = ctx as CanvasRenderingContext2D & {
          roundRect?: (x: number, y: number, w: number, h: number, r: number) => void;
        };
        if (typeof ctxWithRoundRect.roundRect === 'function') {
          ctxWithRoundRect.roundRect(x, y, barWidth, barHeight, radius);
        } else {
          ctx.rect(x, y, barWidth, barHeight);
        }
        ctx.fill();
      }

      frameId = requestAnimationFrame(draw);
    };
    frameId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      try {
        source.disconnect();
        analyser.disconnect();
      } catch {
        // ignore
      }
      audioCtx.close().catch(() => undefined);
    };
  }, [stream, barColor]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minWidth: 0,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        px: 0.5,
        position: 'relative',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </Box>
  );
}
